import { useEffect, useState } from "react";
import { Constants } from "../../../constants";
import { useManageUsersContext } from "../../../context/ManageUsersContext";
import useAlert from "../../../hooks/useAlert";
import { IFormattedMessage } from "../../../models/message.interface";
import { AddOrganizationUserFailureReason } from "../../../models/results/add-org-user-result.interface";
import { IAddPatient } from "../../../models/users.interface";
import usersService from "../../../services/users.service";
import AlertModal from "../../Shared/AlertModal/AlertModal";
import { Loader } from "@shltelemedicine/shl-ui-lib";
import AddPatientModal from "./AddPatientModal/AddPatientModal";
import ManagePatientsHeader from "./ManagePatientsHeader/ManagePatientsHeader";
import ManagePatientsTable from "./ManagePatientsTable/ManagePatientsTable";
import PatientDetailsPortal from "./PatientDetailsPortal/PatientDetailsPortal";

const ManagePatients = () => {
  const {
    state: { loading },
    actions: { loadUsers },
  } = useManageUsersContext();

  const {
    showErrorAlert,
    alertProps,
    alertMessage,
    getAlertMessageAndSupportContent,
  } = useAlert();
  const [showAddPatientModal, setShowAddPatientModal] = useState(false);

  useEffect(() => {
    (async () => {
      await loadUsers({ isPatients: true });
    })();
  }, []); // eslint-disable-line

  const addPatientHandler = async (newPatient: IAddPatient) => {
    const { success, failReason, errorCode } = await usersService.addPatient(
      newPatient
    );
    if (!success) {
      const { id, defaultMessage, values } = getAddPatientFailureMsg(
        failReason
      );
      const { message, support } = getAlertMessageAndSupportContent(
        id,
        defaultMessage,
        values,
        errorCode
      );
      showErrorAlert(message, support);
      return;
    }
    setShowAddPatientModal(false);
    await loadUsers({ isPatients: true });
  };

  const handleShowAddPatientModal = () => setShowAddPatientModal(true);
  const handleHideAddPatientModal = () => setShowAddPatientModal(false);

  const getAddPatientFailureMsg = (
    failureReason: AddOrganizationUserFailureReason | undefined
  ): IFormattedMessage => {
    if (!failureReason) return {} as IFormattedMessage;

    switch (failureReason) {
      case AddOrganizationUserFailureReason.PatientAccountExists:
        return {
          id: "ERROR.ADD_PATIENT.EMAIL_EXISTS",
          defaultMessage:
            "This username (email address) cannot be used. It already exists in our system, please verify the username (email address).<br></br>Please try again.",
          values: {
            br: () => <br></br>,
          },
        };
      default:
        return {
          id: "ERROR.Add_PATIENT",
          defaultMessage:
            "An error occurred while creating the new patient account.<br></br>Please try again.",
          values: {
            br: () => <br></br>,
          },
        };
    }
  };

  return (
    <>
      <div className="d-flex flex-column h-100 ">
        <ManagePatientsHeader
          itemsPerPage={Constants.TABLE_ITEMS_PER_PAGE}
          onAddPatient={handleShowAddPatientModal}
        />
        <div className="position-relative overflow-auto flex-grow-1">
          {loading ? (
            <div className="p-4">
              <Loader />
            </div>
          ) : (
            <ManagePatientsTable
              itemsPerPage={Constants.TABLE_ITEMS_PER_PAGE}
            />
          )}
        </div>
      </div>
      <PatientDetailsPortal />
      <AddPatientModal
        show={showAddPatientModal}
        onSave={addPatientHandler}
        onClose={handleHideAddPatientModal}
      />
      <AlertModal {...alertProps}>{alertMessage}</AlertModal>
    </>
  );
};

export default ManagePatients;
